import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Building2, FileSearch, FileWarning, Package, Table } from 'lucide-react'
import { getBundleFile, getBundles, getDraftManifest } from '@/api/bundles'
import { Banner } from '@/components/Banner'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import type {
  BundleSummary,
  ClientsFile,
  DocumentTypesFile,
  DraftManifestEntry,
  LookupTableFile,
  ProductTypesFile,
} from '@/types/bundle'
import { errorMessage } from '@/lib/apiErrors'
import { fileKeyLabel } from '@/lib/fileKeys'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import { RecentActivityList } from './components/RecentActivityList'
import { SummaryCard } from './components/SummaryCard'

export function DashboardScreen() {
  const navigate = useNavigate()
  const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: getBundles })
  const draftManifestQuery = useQuery({ queryKey: ['draft-manifest'], queryFn: getDraftManifest })

  if (bundlesQuery.isPending || draftManifestQuery.isPending) {
    return <DashboardSkeleton />
  }

  if (bundlesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load bundles">
        {errorMessage(bundlesQuery.error)}
      </Banner>
    )
  }

  if (draftManifestQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load draft manifest">
        {errorMessage(draftManifestQuery.error)}
      </Banner>
    )
  }

  const bundles = bundlesQuery.data
  if (bundles.length === 0) {
    return (
      <EmptyState
        icon={FileWarning}
        headline="No configuration published yet"
        description="Once a bundle is published, its live status and section counts will appear here."
        action={{ label: 'Load current bundle', onClick: () => bundlesQuery.refetch() }}
      />
    )
  }

  return (
    <DashboardContent
      bundles={bundles}
      draftManifest={draftManifestQuery.data}
      onReviewAndPublish={() => navigate('/diff-validation')}
    />
  )
}

interface DashboardContentProps {
  bundles: BundleSummary[]
  draftManifest: DraftManifestEntry[]
  onReviewAndPublish: () => void
}

// Split out from DashboardScreen so its 4 bundle-file queries (which depend
// on a live version DashboardScreen has already confirmed exists) are called
// unconditionally on every render of this component - DashboardScreen itself
// early-returns before a live version is known, which would otherwise break
// the rules of hooks if these queries lived there instead.
function DashboardContent({ bundles, draftManifest, onReviewAndPublish }: DashboardContentProps) {
  const liveVersion = bundles.find((bundle) => bundle.isLive)?.version

  const productTypesQuery = useQuery({
    queryKey: ['bundle-file', liveVersion, 'product_types'],
    queryFn: () => getBundleFile<ProductTypesFile>(liveVersion as string, 'product_types'),
    enabled: !!liveVersion,
  })
  const documentTypesQuery = useQuery({
    queryKey: ['bundle-file', liveVersion, 'document_types'],
    queryFn: () => getBundleFile<DocumentTypesFile>(liveVersion as string, 'document_types'),
    enabled: !!liveVersion,
  })
  const lookupTableQuery = useQuery({
    queryKey: ['bundle-file', liveVersion, 'lookup_table'],
    queryFn: () => getBundleFile<LookupTableFile>(liveVersion as string, 'lookup_table'),
    enabled: !!liveVersion,
  })
  const clientsQuery = useQuery({
    queryKey: ['bundle-file', liveVersion, 'clients'],
    queryFn: () => getBundleFile<ClientsFile>(liveVersion as string, 'clients'),
    enabled: !!liveVersion,
  })

  if (!liveVersion) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="No live bundle version found">
        The bundle list did not identify a currently live version.
      </Banner>
    )
  }

  const fileQueries = [
    { label: 'Products', query: productTypesQuery },
    { label: 'Documents', query: documentTypesQuery },
    { label: 'Lookup Table', query: lookupTableQuery },
    { label: 'Clients', query: clientsQuery },
  ]

  const failedQuery = fileQueries.find((entry) => entry.query.isError)
  if (failedQuery) {
    return (
      <Banner variant="danger" icon={AlertCircle} title={`Failed to load ${failedQuery.label}`}>
        {errorMessage(failedQuery.query.error)}
      </Banner>
    )
  }

  if (fileQueries.some((entry) => entry.query.isPending)) {
    return <DashboardSkeleton />
  }

  const changedFiles = draftManifest.filter((entry) => entry.exists)
  const productTypesCount = Object.keys(productTypesQuery.data?.product_types ?? {}).length
  const documentTypesCount = Object.keys(documentTypesQuery.data?.document_types ?? {}).length
  const lookupTableCount = lookupTableQuery.data?.entries.length ?? 0
  const clientsCount = Object.keys(clientsQuery.data?.firms ?? {}).length

  return (
    <div className="flex flex-col gap-6">
      {changedFiles.length > 0 && (
        <Banner
          variant="warning"
          icon={AlertCircle}
          title={
            changedFiles.length === 1
              ? `You have unpublished changes in ${fileKeyLabel(changedFiles[0].fileKey)}`
              : `You have unpublished changes in ${changedFiles.length} sections`
          }
          action={{ label: 'Review & Publish', onClick: onReviewAndPublish }}
        />
      )}

      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          icon={Building2}
          label="Clients"
          count={clientsCount}
          to="/client-overrides"
        />
        <SummaryCard
          icon={Package}
          label="Products"
          count={productTypesCount}
          to="/product-types"
        />
        <SummaryCard
          icon={FileSearch}
          label="Documents"
          count={documentTypesCount}
          to="/document-types"
        />
        <SummaryCard
          icon={Table}
          label="Lookup Table Entries"
          count={lookupTableCount}
          to="/lookup-table"
        />
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="flex items-center gap-1 text-header font-semibold">
          Recent activity
          <InfoTooltip text="The most recent published versions of the configuration, newest first." />
        </h2>
        <RecentActivityList bundles={bundles} />
      </div>
    </div>
  )
}
